/**
 * FDarNotesCard — collapsible F-DAR (Focus / Data / Action / Response) mini-note
 * widget. Iter 68.
 *
 * Drop-in 4-textarea panel that can be embedded in any page (PatientInterview,
 * ObjectiveAssessment, ClinicalNote, FitnessAssessment, …) for an ad-hoc rehab
 * note in F-DAR format. The parent owns the state and persists the four fields
 * on its existing payload.
 *
 * Props:
 *   value     {focus, data, action, response}
 *   onChange  (key, val) => void   — keys: focus | data | action | response
 *   readOnly  bool (hides textareas, shows plain text)
 *   discipline  passed through to QuickPhrasePicker for context-aware macros
 *   testidPrefix  customise testids — default "fdar-mini"
 *   defaultOpen   start expanded (default false; saves vertical space)
 */
import React, { useState } from 'react';
import QuickPhrasePicker, { appendPhrase } from './QuickPhrasePicker';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';

const LETTERS = {
  focus:    { letter: 'F', title: 'Focus',    hint: 'The chief concern, problem, or significant event this entry addresses.' },
  data:     { letter: 'D', title: 'Data',     hint: 'Subjective + objective observations supporting the focus.' },
  action:   { letter: 'A', title: 'Action',   hint: 'Interventions performed — manual therapy, exercise, education, modalities.' },
  response: { letter: 'R', title: 'Response', hint: 'Patient response — pain, ROM, functional change, plan adjustment.' },
};

export default function FDarNotesCard({
  value = {}, onChange, readOnly = false, discipline = 'physiotherapy',
  testidPrefix = 'fdar-mini', defaultOpen = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const v = value || {};
  const isEmpty = !v.focus && !v.data && !v.action && !v.response;

  return (
    <div className="surface border border-soft rounded-lg overflow-hidden" data-testid={testidPrefix}>
      <button type="button"
              onClick={() => setOpen(o => !o)}
              data-testid={`${testidPrefix}-toggle`}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-bg-secondary/30 transition">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-brand/10 text-brand font-bold text-xs flex items-center justify-center">
            F·D·A·R
          </div>
          <div>
            <div className="font-heading font-semibold text-sm inline-flex items-center gap-2">
              <FileText className="w-4 h-4 text-brand"/> F-DAR Notes
              {!isEmpty && <span className="badge-success text-[10px]">filled</span>}
            </div>
            <div className="text-[11px] text-fg-muted">Focus · Data · Action · Response (rehab + nursing standard).</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-fg-muted"/> : <ChevronDown className="w-4 h-4 text-fg-muted"/>}
      </button>

      {open && (
        <div className="border-t border-soft p-4 space-y-3">
          {Object.entries(LETTERS).map(([key, meta]) => (
            <div key={key} className="border border-soft rounded-md p-3">
              <div className="flex items-start gap-3 mb-1.5">
                <div className="shrink-0 w-7 h-7 rounded-md bg-brand text-white font-heading font-bold flex items-center justify-center text-sm">
                  {meta.letter}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h4 className="font-semibold text-sm text-fg">{meta.title}</h4>
                    {!readOnly && onChange && (
                      <QuickPhrasePicker
                        discipline={discipline}
                        onPick={(t) => onChange(key, appendPhrase(v[key], t))}
                        testid={`${testidPrefix}-${key}-phrases`}
                      />
                    )}
                  </div>
                  <p className="text-[10px] text-fg-muted">{meta.hint}</p>
                </div>
              </div>
              {readOnly ? (
                <div className="text-xs text-fg whitespace-pre-wrap min-h-[40px]">
                  {v[key] || <span className="text-fg-muted italic">— not recorded —</span>}
                </div>
              ) : (
                <textarea className="input min-h-[70px] !text-sm"
                          value={v[key] || ''}
                          onChange={(e) => onChange(key, e.target.value)}
                          data-testid={`${testidPrefix}-${key}`}/>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
